import React, { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Link,
  List,
  ListSubheader,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';

import Logo from 'src/components/Logo';
import NavItem from './NavItem';
import UserNavConfig from './UserNav';

function renderNavItems({ items, activeRole, ...rest }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, activeRole, ...rest }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({ acc, pathname, item, activeRole, depth = 0 }) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath({ path: item.href, end: false }, pathname);
    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={key}
        info={item.info}
        open={Boolean(open)}
        title={item.title}
        role={item.role}
        activeRole={activeRole}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
          activeRole
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={key}
        info={item.info}
        title={item.title}
        role={item.role}
        activeRole={activeRole}
      />
    );
  }

  return acc;
}

function NavBar({ openMobile, onMobileClose }) {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const location = useLocation();
  const { user, activeRole, businessInfo } = useSelector(state => state.account);
  const navConfig = UserNavConfig;

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        {!isLgUp && (
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink to="/">
              <Logo height="60px" />
            </RouterLink>
          </Box>
        )}
        <Box p={2}>
          <Box display="flex" justifyContent="center">
            <RouterLink to="/app/account">
              <Avatar
                alt="User"
                src={user?.avatar}
                sx={{ width: 64, height: 64, cursor: 'pointer' }}
              />
            </RouterLink>
          </Box>
          <Box mt={2} textAlign="center">
            <Link
              component={RouterLink}
              to="/app/account"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {user?.name}
            </Link>
            <Typography variant="body2" color="textSecondary">
              {activeRole}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box p={2}>
          {navConfig
            .filter((nConfig) => {
              if (!nConfig.businesstype || nConfig.businesstype.length === 0) {
                return true;
              }
              return nConfig.businesstype.includes(businessInfo?.type);
            })
            .map((config) => (
              <List
                key={config.subheader}
                subheader={
                  <ListSubheader disableGutters disableSticky>
                    {config.subheader}
                  </ListSubheader>
                }
              >
                {renderNavItems({
                  items: config.items,
                  pathname: location.pathname,
                  activeRole: `${activeRole}`
                })}
              </List>
            ))}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      {!isLgUp && (
        <Drawer
          anchor="left"
          PaperProps={{ sx: { width: 256 } }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      )}
      {isLgUp && (
        <Drawer
          anchor="left"
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: 256,
              top: 64,
              height: 'calc(100% - 64px)'
            }
          }}
        >
          {content}
        </Drawer>
      )}
    </>
  );
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
